'use client';

import { useTranslate } from '@tolgee/react';
import { LogOut, Settings, ShieldCheck, User } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getLink, AdminPages, Pages } from '@/lib/app-links';
import { useUser } from '@/lib/auth-hooks';
import { signOutAndRedirect } from '@/lib/sign-out';
import { cn } from '@/lib/utils';
import { canAccessAdminPages } from '@/permissions/general';

import { useUserSettings } from '../hooks/useUserSettings';

function getInitials(firstName?: string | null, lastName?: string | null): string | null {
  const first = firstName?.trim().charAt(0).toUpperCase();
  const last = lastName?.trim().charAt(0).toUpperCase();
  if (first && last) return `${first}${last}`;
  if (first) return first;
  return null;
}

interface AppAvatarMenuProps {
  isOffline?: boolean;
}

export function AppAvatarMenu({ isOffline = false }: AppAvatarMenuProps) {
  const { user } = useUser();
  const { t } = useTranslate();
  const { data: settings } = useUserSettings();
  const router = useRouter();
  const initials = getInitials(user?.firstName, user?.lastName);
  const avatarUrl = settings?.avatarUrl;
  const canAccessAdmin = isOffline || canAccessAdminPages({ role: user?.publicMetadata?.role });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex size-8 items-center justify-center overflow-hidden rounded-full bg-lime-600 text-white transition-colors hover:bg-lime-500 focus:outline-none"
          aria-label={t('user-settings.app-avatar-menu.aria-label', 'Open app menu')}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="size-full object-cover" />
          ) : initials ? (
            <span className="text-xs font-bold">{initials}</span>
          ) : (
            <User className="size-4" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px] border border-gray-700 bg-gray-800">
        {canAccessAdmin && (
          <DropdownMenuItem asChild>
            <Link
              href={getLink(AdminPages.Admin)}
              className={cn(
                'flex items-center px-3 py-2 text-sm text-gray-300',
                'hover:bg-gray-700 hover:text-gray-200',
                'focus:bg-gray-700 focus:text-gray-200',
                'cursor-pointer',
              )}>
              <ShieldCheck className="mr-2 size-4" />
              <span>{t('nav.admin', 'Admin')}</span>
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link
            href={getLink(Pages.Settings)}
            className={cn(
              'flex items-center px-3 py-2 text-sm text-gray-300',
              'hover:bg-gray-700 hover:text-gray-200',
              'focus:bg-gray-700 focus:text-gray-200',
              'cursor-pointer',
            )}>
            <Settings className="mr-2 size-4" />
            <span>{t('nav.settings')}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={async () => {
            await signOutAndRedirect(router);
          }}
          className={cn(
            'flex items-center px-3 py-2 text-sm text-gray-300',
            'hover:bg-gray-700 hover:text-gray-200',
            'focus:bg-gray-700 focus:text-gray-200',
            'cursor-pointer',
          )}>
          <LogOut className="mr-2 size-4" />
          <span>{t('nav.sign-out', 'Sign out')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
